import {
  AuthUtils,
  CostCenter,
  FlowjoCommons,
  LicenseUtils,
  Product,
} from '@portal/portal-core';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { AuthorizedDevices as AuthorizedDeviceActions } from '../../actions';
import { PRODUCT_INFO } from '../../constants/products';
import {
  currentTabSelector,
  selectAllAuthorizedDevices,
  selectAllProductLicenses,
  selectAuthorizedDeviceRemainingDeactivationsForCurrentProduct,
  selectAuthorizedDevicesForCurrentProduct,
  selectAuthorizedDevicesByIdentifierType,
  selectAuthorizedDevicesSagaState,
  selectCostCenters,
  selectCurrentProductLicenseDetails,
  selectIsCostCenterInstitutional,
  selectMaxAnnualDeviceDeactivationsForCurrentProduct,
  selectMaximumAuthorizedDevicesForCurrentProduct,
  selectProductLicense,
  selectSelf,
  selectUsers,
} from '../../selectors';
import {
  APPLICATION_NAME,
  downloadTextFileAsync,
  formatDate,
  navigate,
  pluralize,
} from '../../utils';
import FormErrorsModal from '../cloud-admin/tab-content-area/selected-tab-content/form-errors-modal';
import Loading from '../loading';
import { FjFlexContainer, FjInput, FjTooltip, fjButtonTypes, fjModalSizes } from '../primitives';
import { AlertMessage, Button, Modal } from '../styled-primitives';
import { CreateDeviceModal } from './create-device-modal';
import { CreateEotModal } from './create-extended-offline-token-modal';
import DeviceList from './device-list';
import { ExportEotModal } from './export-extended-offline-token-modal';
import { InfoEotModal } from './info-extended-offline-token-modal';

const { NAMES_LICENSED_PRODUCTS } = Product;

// ------------------------------------------------------------------------------------------------
// Local types
// ------------------------------------------------------------------------------------------------

interface SelfUser {
  id: string;
  primaryEmail?: string;
  firstname?: string;
  lastname?: string;
}

interface UserSeat {
  startDate?: string;
  endDate?: string;
  suspended?: boolean;
}

interface License {
  id: string;
  name?: string;
  costCenterId?: string;
  activityState?: string;
  startDate?: string;
  endDate?: string;
  users?: Record<string, UserSeat>;
}

interface LicenseDetails {
  costCenterName?: string;
  costCenterId?: string;
  productAccessId?: string;
  productName?: string;
  productMarketingName?: string;
}

interface CostCenterInfo {
  countryCode?: string;
  owner?: string;
}

interface UserInfo {
  firstname?: string;
  lastname?: string;
  primaryEmail?: string;
}

interface SagaStatus {
  loading: boolean;
  success?: boolean;
  result?: unknown;
}

interface AuthorizedDevicesSagaState {
  createDeviceStatus: SagaStatus;
  exportEotStatus: SagaStatus;
  [key: string]: SagaStatus;
}

interface DevicesByType {
  hwas: Device[];
  fingerprint: Device[];
  total: number;
}

interface AuthorizedDeviceWrapper {
  authorizedDeviceJson: Device;
  extendedOfflineToken?: ExtendedOfflineToken;
}

type AllAuthorizedDevices = Record<string, AuthorizedDeviceWrapper>;

interface LicenseStatus {
  isActive: boolean;
  components: string[];
  displayText: string;
  userSeatEndDate?: string;
  licenseEndDate?: string;
}

type AnalyzedLicense = ReturnType<typeof LicenseUtils.analyzeLicense>;

interface ManageDevicesProps {
  self: SelfUser | null;
  productName: string;
  authorizedDevicesSagaState: AuthorizedDevicesSagaState;
  authorizedDevices: Device[];
  devicesByType: DevicesByType;
  maximumDevices: number;
  removalsRemaining: number;
  maxAnnualDeviceDeactivations: number;
  allAuthorizedDevices: AllAuthorizedDevices;
  costCenterIsInstitutional?: boolean;
  productLicenseInfo: License | null;
  licenseDetails?: LicenseDetails;
  allProductLicenses?: Record<string, License[]>;
  costCenters?: Record<string, CostCenterInfo>;
  users?: Record<string, UserInfo>;
  deactivateAuthorizedDevice: (
    userId: string,
    id: string,
    allAuthorizedDevices: AllAuthorizedDevices,
  ) => void;
  createExtendedOfflineToken: (
    userId: string,
    id: string,
    startDate: Date,
    endDate: Date,
    productAccessId: string,
  ) => void;
  exportExtendedOfflineToken: (
    primaryEmail: string,
    password: string,
    costCenterId: string,
    productAccessId: string,
    eotId: string,
  ) => unknown;
  createAuthorizedDevice: (
    userId: string,
    costCenterId: string,
    productAccessId: string,
    fingerprint: string,
    deviceName: string,
  ) => void;
  renameAuthorizedDevice: (
    userId: string,
    id: string,
    newName: string,
    authorizedDevices: Device[],
  ) => void;
}

// ------------------------------------------------------------------------------------------------
// Styled components
// ------------------------------------------------------------------------------------------------

const TitleContainer = styled.div`
  padding: ${({ theme: { grid } }) => `${grid.units(1)} ${grid.units(1.5)} 0 ${grid.units(1.5)}`};
`;

const SubtitleContainer = styled.div`
  padding: ${({ theme: { grid } }) => `${grid.units(0.25)} ${grid.units(1.5)}`};
  margin-bottom: ${({ theme: { grid } }) => grid.units(0.5)};
`;

const LicenseInfoContainer = styled.div<{ isInactive?: boolean }>`
  padding: ${({ theme: { grid } }) => grid.units(1)};
  margin-bottom: ${({ theme: { grid } }) => grid.units(1)};
  background-color: ${({ theme, isInactive }) =>
    isInactive
      ? (theme.colors as unknown as Record<string, string | undefined>).errorBackground || '#ffebee'
      : theme.colors.tabBackground};
  font-size: 0.9em;
  color: ${({ theme }) => theme.colors.black};
  line-height: 1.6;
  border: ${({ theme, isInactive }) =>
    isInactive ? `2px solid ${theme.colors.error || '#d32f2f'}` : 'none'};
`;

const LicenseDetailRow = styled.div`
  margin-bottom: ${({ theme: { grid } }) => grid.units(0.25)};

  &:last-child {
    margin-bottom: 0;
  }
`;

const LicenseDetailsToggle = styled.a<{ $expanded?: boolean }>`
  color: ${({ theme }) => theme.colors.selectableLightBg};
  cursor: pointer;
  text-decoration: none;
  font-size: 0.9em;
  display: inline-flex;
  align-items: center;
  margin-bottom: ${({ theme: { grid } }) => grid.units(0.5)};
  margin-top: ${({ theme: { grid } }) => grid.units(0.5)};

  &:hover {
    text-decoration: underline;
  }

  &::before {
    content: '${({ $expanded }) => ($expanded ? '▼' : '▶')}';
    margin-right: 0.5em;
    font-size: 0.7em;
  }
`;

const StyledAlertMsg = styled(AlertMessage)`
  margin-left: ${({ theme: { grid } }) => grid.units(1.5)};
  margin-right: ${({ theme: { grid } }) => grid.units(1.5)};
  margin-top: ${({ theme: { grid } }) => grid.units(0.5)};
`;

const StyledModal = styled(Modal)`
  ${Modal.components.Body} {
    overflow: auto;
    padding: ${({ theme: { grid } }) =>
      `${grid.units(1)} ${grid.units(0.5)} ${grid.units(1)} ${grid.units(0.5)}`};
  }
  ${Modal.components.InnerWrapper} {
    height: auto;
  }
  ${Modal.components.Header} {
    margin-bottom: ${({ theme: { grid } }) => grid.units(0.25)};
  }
`;

const Header = styled.header`
  display: flex;
  background-color: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  padding: 0 ${({ theme }) => theme.grid.units(0.5)};
  height: ${({ theme }) => theme.grid.units(1)};
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.3);
`;

const DeviceListContainer = styled(FjFlexContainer)`
  padding: 0 ${({ theme }) => theme.grid.units(1)};
`;

const ConfirmationBox = styled.div`
  margin-top: ${({ theme: { grid } }) => grid.units(1)};
  padding: ${({ theme: { grid } }) => grid.units(1)};
  background-color: #fff3cd;
  border: 1px solid #ffca2c;
  border-radius: 4px;
`;

export const ManageDevices = ({
  self,
  productName,
  authorizedDevicesSagaState,
  authorizedDevices,
  devicesByType,
  maximumDevices,
  removalsRemaining,
  maxAnnualDeviceDeactivations,
  allAuthorizedDevices,
  costCenterIsInstitutional,
  productLicenseInfo,
  licenseDetails,
  allProductLicenses,
  costCenters,
  users,
  deactivateAuthorizedDevice,
  createExtendedOfflineToken,
  exportExtendedOfflineToken,
  createAuthorizedDevice,
  renameAuthorizedDevice,
}: ManageDevicesProps): React.ReactElement => {
  // Derived from props every render (was getDerivedStateFromProps).
  const hasCurrentEot = authorizedDevices.reduce(
    (acc: boolean, device: Device) => acc || !!device.extendedOfflineToken,
    false,
  );
  const isLoading = Object.values(authorizedDevicesSagaState).reduce(
    (acc: boolean, { loading }: SagaStatus) => acc || loading,
    false,
  );
  const productInfo = PRODUCT_INFO[productName];

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [showCreateDeviceModal, setShowCreateDeviceModal] = useState(false);
  const [showInfoEotModal, setShowInfoEotModal] = useState(false);
  const [showCreateEotModal, setShowCreateEotModal] = useState(false);
  const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);
  const [showExportEotModal, setShowExportEotModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessages, setErrorModalMessages] = useState<string[]>([]);
  const [errorModalTitle, setErrorModalTitle] = useState('Error');
  const [showLicenseDetails, setShowLicenseDetails] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  // Stores the specific license if licenseId is in URL
  const [specificLicense, setSpecificLicense] = useState<License | null>(null);
  // Analyzed version of specific license
  const [specificLicenseAnalyzed, setSpecificLicenseAnalyzed] = useState<AnalyzedLicense | null>(
    null,
  );

  const closeModal = (): void => navigate('/');

  const getProductName = (): string => Product.PRODUCT_MARKETING_NAMES[productName];

  const isRemovalDisabled = (): boolean => removalsRemaining < 1;

  // ERROR MODAL
  const openErrorModal = (messages: string[], title: string): void => {
    setShowErrorModal(true);
    setErrorModalMessages(messages);
    setErrorModalTitle(title);
  };

  const closeErrorModal = (): void => {
    setShowErrorModal(false);
    setErrorModalMessages([]);
    setErrorModalTitle('Error');
  };

  const renderErrorModal = (): React.ReactNode =>
    showErrorModal && (
      <FormErrorsModal
        errorMessageArray={errorModalMessages}
        title={errorModalTitle}
        onRequestClose={closeErrorModal}
      />
    );

  const renameDevice =
    ({ id, userId }: Device) =>
      (newName: string): void =>
        renameAuthorizedDevice(userId, id, newName, authorizedDevices);

  // REMOVE DEVICE
  const openRemoveDeviceConfirmModal = (device: Device): void => {
    setSelectedDevice(device);
    setShowConfirmRemovalModal(true);
    setConfirmationText('');
  };

  const closeRemoveDeviceModal = (): void => {
    setShowConfirmRemovalModal(false);
    setConfirmationText('');
  };

  const removeDevice = ({ id, userId }: Device): void => {
    deactivateAuthorizedDevice(userId, id, allAuthorizedDevices);
    closeRemoveDeviceModal();
  };

  // EXPORT EOT
  const openExportEotModal = (device: Device): void => {
    setSelectedDevice(device);
    setShowExportEotModal(true);
  };

  const closeExportEotModal = (): void => {
    setSelectedDevice(null);
    setShowExportEotModal(false);
  };

  const handleExportEotAccept = async (password: string): Promise<void> => {
    await exportExtendedOfflineToken(
      self!.primaryEmail!,
      password,
      productLicenseInfo!.costCenterId!,
      productLicenseInfo!.id,
      selectedDevice!.extendedOfflineToken!.id,
    );
  };

  const renderExportEotModal = (): React.ReactNode =>
    showExportEotModal && (
      <ExportEotModal
        device={selectedDevice!}
        acceptCallback={handleExportEotAccept}
        cancelCallback={closeExportEotModal}
      />
    );

  // EOT INFO MODAL
  const handleShowInfoEotModal = (device: Device): void => {
    setSelectedDevice(device);
    setShowInfoEotModal(true);
  };

  const handleCloseInfoEotModal = (): void => {
    setShowInfoEotModal(false);
    setShowCreateEotModal(false);
  };

  const renderInfoEotModal = (): React.ReactNode =>
    showInfoEotModal && (
      <InfoEotModal
        okCallback={handleCloseInfoEotModal}
        device={selectedDevice!}
        productName={getProductName()}
        defaultDays={productInfo.eot!.defaultDays}
        isFlowJo={productName === Product.NAMES.flowJo}
      />
    );

  // CREATE EOT MODAL
  const handleOpenCreateEotModal = (device: Device): void => {
    setSelectedDevice(device);
    setShowCreateEotModal(true);
  };

  const handleCloseCreateEotModal = (): void => setShowCreateEotModal(false);

  const handleCreateEotModalAccept = async (): Promise<void> => {
    const { userId, id } = selectedDevice!;
    const { defaultDays } = productInfo.eot!;
    const { id: productAccessId } = productLicenseInfo!;
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + defaultDays);

    createExtendedOfflineToken(userId, id, startDate, endDate, productAccessId);
    handleShowInfoEotModal(selectedDevice!);
  };

  const renderCreateEotModal = (): React.ReactNode =>
    showCreateEotModal && (
      <CreateEotModal
        device={selectedDevice!}
        defaultDays={productInfo.eot!.defaultDays}
        acceptCallback={handleCreateEotModalAccept}
        cancelCallback={handleCloseCreateEotModal}
        productName={getProductName()}
        isFlowJo={productName === Product.NAMES.flowJo}
      />
    );

  // CREATE DEVICE MODAL
  const openCreateDeviceModal = (): void => {
    setSelectedDevice(null);
    setShowCreateDeviceModal(true);
  };

  const closeCreateDeviceModal = (): void => setShowCreateDeviceModal(false);

  const handleCreateDeviceAcceptButtonClick = (fingerprint: string, deviceName: string): void => {
    const { costCenterId, id: productAccessId } = productLicenseInfo!;
    const { id: userId } = self!;
    try {
      createAuthorizedDevice(userId, costCenterId!, productAccessId, fingerprint, deviceName);
      // TODO - enable EOT creation and export during device creation
      // if (createEot) handleCreateEotModalAccept();
      // if (exportEot) openExportEotModal();
    } catch (err) {
      let error: string;
      switch (typeof err) {
        case 'string':
          error = err;
          break;
        case 'object':
          error = String(err);
          break;
        default:
          error = 'An unknown error occurred.';
      }
      openErrorModal(
        ['There was an error.', error, 'Please try again.'],
        'Unable to Create Device',
      );
    }
  };

  const renderCreateDeviceModal = (): React.ReactNode =>
    showCreateDeviceModal && (
      <CreateDeviceModal
        acceptCallback={(fingerprint: string, deviceName: string) =>
          handleCreateDeviceAcceptButtonClick(fingerprint, deviceName)
        }
        cancelCallback={closeCreateDeviceModal}
        isLoading={authorizedDevicesSagaState.createDeviceStatus.loading}
        allowEotCreation={!hasCurrentEot}
      />
    );

  const authorizedDeviceRemovalResetDate = (): string | null => {
    if (!productLicenseInfo || !self) return null;

    // Get the user seat's start date, matching backend behavior in authorized-devices.ts
    const userLicense = productLicenseInfo.users?.[self.id];
    const licenseStartDate = userLicense?.startDate || productLicenseInfo.startDate;

    if (!licenseStartDate) return null;

    // Use shared utility if available (requires cloud-core >= 4.32.0); fall back to manual calc
    if (typeof LicenseUtils.getAuthorizedDeviceRemovalResetDate === 'function') {
      const resetDate = LicenseUtils.getAuthorizedDeviceRemovalResetDate(licenseStartDate);
      return resetDate ? formatDate(resetDate) : null;
    }

    // Fallback: replicate backend logic until cloud-core is updated
    const adjustedStart = new Date(LicenseUtils.getCurrentBillingYearStartDate(licenseStartDate));
    adjustedStart.setFullYear(adjustedStart.getFullYear() + 1);
    return formatDate(adjustedStart);
  };

  const calculateLicenseStatus = (
    specificLicenseOverride: License | null = specificLicense,
  ): LicenseStatus | null => {
    // Use specific license if available, otherwise use the default (most permissive)
    const licenseToUse = specificLicenseOverride || productLicenseInfo;

    if (!licenseToUse || !self) return null;

    const userLicense = licenseToUse.users?.[self.id];
    const components: string[] = [];

    // Check user seat status
    const isUserSeatActive = userLicense
      ? AuthUtils.isUserSeatActive(
          userLicense.suspended,
          userLicense.startDate,
          userLicense.endDate,
        )
      : false;

    if (!isUserSeatActive) {
      if (userLicense?.suspended) {
        components.push('User Suspended');
      } else if (userLicense?.endDate && new Date(userLicense.endDate) < new Date()) {
        components.push('User Seat Expired');
      } else if (userLicense?.startDate && new Date(userLicense.startDate) > new Date()) {
        components.push('User Seat Not Started');
      } else {
        components.push('User Seat Inactive');
      }
    } else {
      components.push('User Seat Active');
    }

    // Check product access status
    const isProductAccessActive = LicenseUtils.isProductAccessActive(licenseToUse);

    if (!isProductAccessActive) {
      if (licenseToUse.activityState !== 'active') {
        components.push(`License ${licenseToUse.activityState || 'Inactive'}`);
      } else if (licenseToUse.endDate && new Date(licenseToUse.endDate) < new Date()) {
        components.push('License Expired');
      } else if (licenseToUse.startDate && new Date(licenseToUse.startDate) > new Date()) {
        components.push('License Not Started');
      } else {
        components.push('License Inactive');
      }
    } else {
      components.push('License Active');
    }

    const isActive = isUserSeatActive && isProductAccessActive;

    return {
      isActive,
      components,
      displayText: isActive ? 'Active' : 'Inactive',
      userSeatEndDate: userLicense?.endDate,
      licenseEndDate: licenseToUse.endDate,
    };
  };

  // componentDidMount
  useEffect(() => {
    // @TODO figure out why can't filter on this
    const names = Object.values(NAMES_LICENSED_PRODUCTS).filter(
      (p) => p !== NAMES_LICENSED_PRODUCTS.facsWorkflowManager,
    );

    if (!names.includes(productName)) {
      closeModal();
    }

    // Check if licenseId is in URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const licenseId = urlParams.get('licenseId');

    if (licenseId && allProductLicenses && self) {
      // Find the specific license across all products
      const allLicenses = allProductLicenses[productName] || [];
      const foundLicense = allLicenses.find((lic) => lic.id === licenseId);

      if (foundLicense) {
        const analyzedLicense = LicenseUtils.analyzeLicense(foundLicense, self.id);
        setSpecificLicense(foundLicense);
        setSpecificLicenseAnalyzed(analyzedLicense);
        // Thread the just-set license into the status check (was the setState callback)
        const licenseStatus = calculateLicenseStatus(foundLicense);
        if (licenseStatus && !licenseStatus.isActive) {
          setShowLicenseDetails(true);
        }
        return;
      }
    }

    // Auto-expand license details if license is inactive (no licenseId URL param path)
    const licenseStatus = calculateLicenseStatus();
    if (licenseStatus && !licenseStatus.isActive) {
      setShowLicenseDetails(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate: react to saga-driven async completions.
  const prevSagaStateRef = useRef(authorizedDevicesSagaState);
  useEffect(() => {
    const prevSagaState = prevSagaStateRef.current;
    prevSagaStateRef.current = authorizedDevicesSagaState;

    const { createDeviceStatus: prevCreateDeviceStatus, exportEotStatus: prevExportEotStatus } =
      prevSagaState;
    const { createDeviceStatus: nextCreateDeviceStatus, exportEotStatus: nextExportEotStatus } =
      authorizedDevicesSagaState;

    const run = async (): Promise<void> => {
      // Create Device
      if (prevCreateDeviceStatus.loading && !nextCreateDeviceStatus.loading) {
        if (nextCreateDeviceStatus.success === true) {
          closeCreateDeviceModal();
        } else {
          openErrorModal(
            ['There was an error.', String(nextCreateDeviceStatus.result), 'Please try again.'],
            'Unable to Create Device',
          );
        }
      }

      // Export EOT
      if (prevExportEotStatus.loading && !nextExportEotStatus.loading) {
        if (nextExportEotStatus.success === true) {
          const { name: deviceName, extendedOfflineToken } = selectedDevice!;
          const { startDate, endDate } = extendedOfflineToken!;
          const { firstname, lastname } = self!;
          const { name: licenseProductName } = productLicenseInfo!;
          const formattedStartDate = formatDate(startDate, 'YYYY-MM-DD');
          const formattedEndDate = formatDate(endDate, 'YYYY-MM-DD');
          const fileName = `${licenseProductName}_Extended_Offline_Token_${formattedStartDate}_${formattedEndDate}_${firstname}_${lastname}_${deviceName}`;
          // result is the exported token text (string) when success === true
          await downloadTextFileAsync(nextExportEotStatus.result as string, fileName, '.bdeot');
          closeExportEotModal();
        } else {
          openErrorModal(
            ['There was an error.', String(nextExportEotStatus.result), 'Please try again.'],
            'Unable to export Extended Offline Token',
          );
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizedDevicesSagaState]);

  const renderRemoveDeviceModal = (): React.ReactNode => {
    const CONFIRMATION_PHRASE = 'I UNDERSTAND';
    const isConfirmed = confirmationText.trim().toUpperCase() === CONFIRMATION_PHRASE;

    return (
      showConfirmRemovalModal && (
        <Modal
          dataTestId="confirm-modal"
          cancel={{
            text: 'Cancel',
            callback: closeRemoveDeviceModal,
          }}
          accept={{
            text: 'Remove Device',
            callback: () => removeDevice(selectedDevice!),
            disabled: !isConfirmed || isRemovalDisabled(),
          }}
          title="Remove Authorized Device"
          size={(fjModalSizes as Record<string, string | undefined>).MD}
          showX
          onClickX={closeRemoveDeviceModal}
        >
          <AlertMessage
            {...({ type: 'warning', 'data-testid': 'logout-warning' } as Record<string, string>)}
          >
            <strong>Sign out first to avoid re-adding this device</strong>
            <br />
            <br />
            Make sure you are signed out of {getProductName()}
            {productName === Product.NAMES.flowJo &&
              (selectedDevice!.identifiers?.hwas ? ' v10' : ' v11')}{' '}
            on this device before continuing. If the device is still signed in, it will be
            automatically reauthorized the next time {getProductName()} connects.
            <br />
            <br />
            This can prevent you from adding a new device if you&apos;re at your maximum device
            limit.
          </AlertMessage>
          <p>
            {productName === Product.NAMES.flowJo ? (
              <>
                <br />
                <strong>What To Know</strong>
                <br />
                <br />
                {selectedDevice!.identifiers?.hwas ? (
                  <>
                    Maximum allowed: <strong>{maximumDevices}</strong> Authorized{' '}
                    {pluralize(maximumDevices, 'Device')} for FlowJo v10.
                  </>
                ) : (
                  <>
                    Maximum allowed: <strong>{maximumDevices}</strong> Authorized{' '}
                    {pluralize(maximumDevices, 'Device')} for FlowJo v11.
                  </>
                )}
              </>
            ) : (
              <>
                Maximum allowed: <strong>{maximumDevices}</strong> Authorized{' '}
                {pluralize(maximumDevices, 'Device')} for {getProductName()}.
              </>
            )}
            <br />
            After this removal, you can only remove devices <strong>{removalsRemaining - 1}</strong>{' '}
            more {pluralize(removalsRemaining - 1, 'time')} this billing period.
          </p>
          <ConfirmationBox>
            <p style={{ marginBottom: '12px', fontWeight: 'bold' }}>
              I confirm I have signed out of {getProductName()}
              {productName === Product.NAMES.flowJo && (
                <> {selectedDevice!.identifiers?.hwas ? 'v10' : 'v11'}</>
              )}{' '}
              on device{' '}
              <strong>
                {selectedDevice!.identifiers?.hwas?.[0] ||
                  selectedDevice!.identifiers?.fingerprint ||
                  'this device'}
              </strong>
            </p>
            <p style={{ marginBottom: '8px', fontSize: '0.9em' }}>
              To confirm you have read and understood the above, please type{' '}
              <strong>{CONFIRMATION_PHRASE}</strong> below:
            </p>
            <FjInput
              value={confirmationText}
              onChange={(value) => setConfirmationText(value)}
              placeholder={`Type "${CONFIRMATION_PHRASE}" to confirm`}
              data-testid="confirm-understanding-input"
              autoFocus
            />
          </ConfirmationBox>
        </Modal>
      )
    );
  };

  // Filter devices to show only those for the specific license if one was selected
  // Use allAuthorizedDevices when filtering for a specific license to get all devices across all licenses
  const filteredDevices: Device[] = specificLicense
    ? Object.keys(allAuthorizedDevices).reduce<Device[]>((acc, deviceId) => {
        const deviceWrapper = allAuthorizedDevices[deviceId];
        const device = deviceWrapper.authorizedDeviceJson;
        if (device.productAccessId === specificLicense.id && device.deactivatedAt === null) {
          const deviceWithEot: Device = {
            ...device,
            extendedOfflineToken: deviceWrapper.extendedOfflineToken,
          };
          return acc.concat([deviceWithEot]);
        }
        return acc;
      }, [])
    : authorizedDevices;

  // Recalculate devicesByType for the filtered devices
  const filteredDevicesByType: DevicesByType = specificLicense
    ? {
        hwas: filteredDevices.filter((device) => device.identifiers?.hwas),
        fingerprint: filteredDevices.filter((device) => device.identifiers?.fingerprint),
        total: filteredDevices.length,
      }
    : devicesByType;

  // Recalculate hasCurrentEot for the filtered devices
  const hasEot = specificLicense
    ? filteredDevices.reduce(
        (acc: boolean, device: Device) => acc || !!device.extendedOfflineToken,
        false,
      )
    : hasCurrentEot;

  const isFlowJo = productName === Product.NAMES.flowJo;
  const licenseStatus = calculateLicenseStatus();

  return (
    <React.Fragment>
      {isLoading && <FjTooltip Component={<Loading />} />}
      {renderErrorModal()}
      {renderRemoveDeviceModal()}
      {renderCreateEotModal()}
      {renderInfoEotModal()}
      {renderCreateDeviceModal()}
      {renderExportEotModal()}
      <StyledModal
        blocking
        size={fjModalSizes.XL}
        cancel={{
          text: 'Close',
          callback: closeModal,
        }}
        onRequestClose={closeModal}
        showX
        onClickX={closeModal}
        title={
          <TitleContainer>
            <b>Manage Devices</b>
          </TitleContainer>
        }
        borderless
      >
        <SubtitleContainer>
          <p>
            When you sign in to {getProductName()} with your {APPLICATION_NAME} account on a new
            computer, that computer is automatically added to your authorized devices.{' '}
            {isFlowJo ? (
              <>
                You may have up to {maximumDevices} authorized {pluralize(maximumDevices, 'device')}{' '}
                for FlowJo v11 and another {maximumDevices} for FlowJo v10 (total of{' '}
                {maximumDevices * 2} devices).
              </>
            ) : (
              <>
                You may have up to {maximumDevices} authorized {pluralize(maximumDevices, 'device')}
                .
              </>
            )}
          </p>
          {isFlowJo && (
            <p>
              Currently, you have <b>{filteredDevicesByType.fingerprint.length}</b> FlowJo v11{' '}
              {pluralize(filteredDevicesByType.fingerprint.length, 'device')} (fingerprint-based)
              and <b>{filteredDevicesByType.hwas.length}</b> FlowJo v10{' '}
              {pluralize(filteredDevicesByType.hwas.length, 'device')} (MAC address-based)
              registered.
            </p>
          )}
          <p>
            {isFlowJo ? (
              <>
                For this license, you are permitted to remove up to {maxAnnualDeviceDeactivations}{' '}
                {pluralize(maxAnnualDeviceDeactivations, 'device')} per year across all your FlowJo
                versions (v10 and v11 combined).
              </>
            ) : (
              <>
                For this license, you are permitted to remove up to {maxAnnualDeviceDeactivations}{' '}
                {pluralize(maxAnnualDeviceDeactivations, 'device')} from your authorized device list
                each year.
              </>
            )}
          </p>
          <p>
            For more information on when your year starts and how authorized device removals work,
            click{' '}
            <a
              href="https://docs.flowjo.com/portal/authorized-devices/"
              target="blank"
              rel="noreferrer"
            >
              <b>here</b>
            </a>
            .
          </p>
          <p>
            You have used <b>{maxAnnualDeviceDeactivations - removalsRemaining}</b>{' '}
            {pluralize(maxAnnualDeviceDeactivations - removalsRemaining, 'removal')} this year.
          </p>
          <p>
            You have <b>{removalsRemaining}</b> {pluralize(removalsRemaining, 'removal')} remaining.
          </p>
          <p>
            Your removals above will reset on <b>{authorizedDeviceRemovalResetDate()}</b>
          </p>

          <p>
            If you are running out of authorized devices, please ensure your {APPLICATION_NAME}{' '}
            credentials are not being shared. Sharing your {APPLICATION_NAME} account is prohibited,
            as documented in our{' '}
            <a href={productInfo?.links.eula} target="_blank" rel="noreferrer">
              <b>EULA</b>.
            </a>
          </p>
          {licenseDetails &&
            (() => {
              // Get license type and cost center info
              const analyzedLicense = specificLicenseAnalyzed;
              const licenseType = analyzedLicense?.type || 'N/A';
              const costCenterType =
                analyzedLicense?.costCenterType ||
                (costCenterIsInstitutional ? 'institutional' : 'individual');
              const activityState =
                specificLicense?.activityState || productLicenseInfo?.activityState;
              const trialPending =
                activityState === CostCenter.ACTIVITY_STATES.pending ? ' (Pending)' : '';

              // Get cost center and country info
              const costCenterId = analyzedLicense?.costCenterId || licenseDetails.costCenterId;
              const costCenter = costCenters?.[costCenterId];
              const countryCode = costCenter?.countryCode;
              const countryName = countryCode
                ? FlowjoCommons.Regional.getCountryName(countryCode)
                : null;
              const countryDisplay = countryName
                ? `${countryName} (${countryCode})`
                : countryCode || 'N/A';

              return (
                <>
                  <LicenseDetailsToggle
                    $expanded={showLicenseDetails}
                    onClick={() => setShowLicenseDetails((prev) => !prev)}
                    data-testid="license-details-toggle"
                    style={{
                      color: licenseStatus && !licenseStatus.isActive ? '#d32f2f' : undefined,
                    }}
                  >
                    License Details {licenseStatus && !licenseStatus.isActive && '⚠'}
                  </LicenseDetailsToggle>
                  {showLicenseDetails && licenseStatus && (
                    <LicenseInfoContainer
                      data-testid="license-info"
                      isInactive={!licenseStatus.isActive}
                    >
                      <LicenseDetailRow>
                        <strong>Product Name:</strong> {licenseDetails.productMarketingName}
                      </LicenseDetailRow>
                      <LicenseDetailRow>
                        <strong>Cost Center:</strong>{' '}
                        {costCenterType === 'institutional'
                          ? `${licenseDetails.costCenterName}`
                          : 'Individual'}
                      </LicenseDetailRow>
                      <LicenseDetailRow>
                        <strong>Owner:</strong>{' '}
                        {(() => {
                          const ownerCostCenter = costCenters?.[costCenterId];
                          const ownerUserId = ownerCostCenter?.owner;
                          const ownerUser = users?.[ownerUserId as string];
                          if (ownerUser) {
                            const { firstname, lastname, primaryEmail } = ownerUser;
                            return (
                              `${firstname || ''} ${lastname || ''}`.trim() +
                              (primaryEmail ? ` (${primaryEmail})` : '')
                            );
                          }
                          return 'N/A';
                        })()}
                      </LicenseDetailRow>
                      <LicenseDetailRow>
                        <strong>License Type:</strong> {licenseType}
                        {trialPending}
                      </LicenseDetailRow>
                      <LicenseDetailRow>
                        <strong>Status:</strong>{' '}
                        <span
                          style={{
                            color: licenseStatus.isActive ? 'green' : '#d32f2f',
                            fontWeight: 'bold',
                          }}
                        >
                          {licenseStatus.displayText}
                        </span>
                      </LicenseDetailRow>
                      <LicenseDetailRow>
                        <strong>
                          {costCenterType === 'institutional'
                            ? 'Group License Expiration:'
                            : 'Expiration:'}
                        </strong>{' '}
                        {licenseStatus.licenseEndDate
                          ? formatDate(licenseStatus.licenseEndDate)
                          : 'N/A'}
                      </LicenseDetailRow>
                      {costCenterType === 'institutional' && (
                        <LicenseDetailRow>
                          <strong>User Seat Expiration:</strong>{' '}
                          {licenseStatus.userSeatEndDate
                            ? formatDate(licenseStatus.userSeatEndDate)
                            : 'not set'}
                        </LicenseDetailRow>
                      )}
                      <LicenseDetailRow>
                        <strong>Country:</strong> {countryDisplay}
                      </LicenseDetailRow>
                      <LicenseDetailRow>
                        <strong>Product Access ID:</strong> {licenseDetails.productAccessId}
                      </LicenseDetailRow>
                    </LicenseInfoContainer>
                  )}
                </>
              );
            })()}
        </SubtitleContainer>
        <DeviceListContainer direction="column">
          <Header>
            <span>Authorized Devices</span>
            {(() => {
              // The "+" button only creates fingerprint-based devices (not HWA)
              // - FlowJo: Check v11 (fingerprint) devices separately since v10/v11 have separate limits
              // - SeqGeq: Don't show button (only uses legacy HWA mechanism)
              // - Other products: Check total device count
              const isSeqGeq = productName === Product.NAMES.seqGeq;

              let canAddDevice = false;
              if (!isSeqGeq) {
                canAddDevice = isFlowJo
                  ? filteredDevicesByType.fingerprint.length < maximumDevices
                  : filteredDevices.length < maximumDevices;
              }

              return (
                canAddDevice &&
                !hasEot && (
                  <FjTooltip
                    Component={<span>Create a new device with fingerprint from Access Portal</span>}
                    showIcon
                  >
                    <Button
                      buttonType={fjButtonTypes.SQUARE}
                      data-testid="add-device-button"
                      disabled={false}
                      onClick={() => openCreateDeviceModal()}
                    >
                      +
                    </Button>
                  </FjTooltip>
                )
              );
            })()}
          </Header>
          {filteredDevices.length > 0 ? (
            <DeviceList
              devices={filteredDevices}
              handleRemoveClick={openRemoveDeviceConfirmModal}
              isRemovalDisabled={isRemovalDisabled()}
              handleCreateEotButtonClick={handleOpenCreateEotModal}
              handleExportEotButtonClick={openExportEotModal}
              handleShowInfoEotModal={handleShowInfoEotModal}
              renameDevice={renameDevice}
              productName={productName}
            />
          ) : (
            <StyledAlertMsg data-testid="no-devices">
              No devices are currently registered to this account.
            </StyledAlertMsg>
          )}
        </DeviceListContainer>
      </StyledModal>
    </React.Fragment>
  );
};

const ManageDevicesContainer = (): React.ReactElement => {
  const self = useAppSelector(selectSelf);
  // Selectors below are built on an untyped base selector and resolve as `unknown`;
  // narrow each to the shape this view consumes at the redux boundary.
  const productName = useAppSelector(currentTabSelector) as string;
  // The saga-state slice is an untyped redux boundary; narrow it to the shape this view uses.
  const authorizedDevicesSagaState = useAppSelector(
    selectAuthorizedDevicesSagaState,
  ) as unknown as AuthorizedDevicesSagaState;
  const authorizedDevices = useAppSelector(selectAuthorizedDevicesForCurrentProduct);
  const devicesByType = useAppSelector(selectAuthorizedDevicesByIdentifierType);
  const maximumDevices = useAppSelector(selectMaximumAuthorizedDevicesForCurrentProduct);
  const removalsRemaining = useAppSelector(
    selectAuthorizedDeviceRemainingDeactivationsForCurrentProduct,
  );
  const maxAnnualDeviceDeactivations = useAppSelector(
    selectMaxAnnualDeviceDeactivationsForCurrentProduct,
  );
  const allAuthorizedDevices = useAppSelector(selectAllAuthorizedDevices) as AllAuthorizedDevices;
  const costCenterIsInstitutional = useAppSelector(selectIsCostCenterInstitutional);
  const productLicenseInfo = useAppSelector(selectProductLicense);
  const licenseDetails = useAppSelector(selectCurrentProductLicenseDetails);
  const allProductLicenses = useAppSelector(selectAllProductLicenses);
  const costCenters = useAppSelector(selectCostCenters) as Record<string, CostCenterInfo>;
  const users = useAppSelector(selectUsers) as Record<string, UserInfo>;

  const dispatch = useAppDispatch();
  const deactivateAuthorizedDevice = (
    userId: string,
    id: string,
    allDevices: AllAuthorizedDevices,
  ) =>
    // Preserves the original positional call: the devices object is passed as the
    // action creator's `costCenterId` argument (untyped runtime boundary).
    dispatch(
      AuthorizedDeviceActions.deactivateAuthorizedDevice(
        userId,
        id,
        allDevices as unknown as string,
      ),
    );
  const createExtendedOfflineToken = (
    userId: string,
    id: string,
    startDate: Date,
    endDate: Date,
    productAccessId: string,
  ) =>
    // The action stores the Date values as-is; cast at the untyped runtime boundary.
    dispatch(
      AuthorizedDeviceActions.createExtendedOfflineToken(
        userId,
        id,
        startDate as unknown as string,
        endDate as unknown as string,
        productAccessId,
      ),
    );
  const exportExtendedOfflineToken = (
    primaryEmail: string,
    password: string,
    costCenterId: string,
    productAccessId: string,
    eotId: string,
  ) =>
    dispatch(
      AuthorizedDeviceActions.exportExtendedOfflineToken(
        primaryEmail,
        password,
        costCenterId,
        productAccessId,
        eotId,
      ),
    );
  const createAuthorizedDevice = (
    userId: string,
    costCenterId: string,
    productAccessId: string,
    fingerprint: string,
    deviceName: string,
  ) =>
    dispatch(
      AuthorizedDeviceActions.createAuthorizedDevice(
        userId,
        costCenterId,
        productAccessId,
        fingerprint,
        deviceName,
      ),
    );
  const renameAuthorizedDevice = (userId: string, id: string, newName: string) =>
    dispatch(AuthorizedDeviceActions.renameAuthorizedDevice(userId, id, newName));

  return (
    <ManageDevices
      self={self}
      productName={productName}
      authorizedDevicesSagaState={authorizedDevicesSagaState}
      authorizedDevices={authorizedDevices}
      devicesByType={devicesByType}
      maximumDevices={maximumDevices}
      removalsRemaining={removalsRemaining}
      maxAnnualDeviceDeactivations={maxAnnualDeviceDeactivations}
      allAuthorizedDevices={allAuthorizedDevices}
      costCenterIsInstitutional={costCenterIsInstitutional}
      productLicenseInfo={productLicenseInfo}
      licenseDetails={licenseDetails}
      allProductLicenses={allProductLicenses}
      costCenters={costCenters}
      users={users}
      deactivateAuthorizedDevice={deactivateAuthorizedDevice}
      createExtendedOfflineToken={createExtendedOfflineToken}
      exportExtendedOfflineToken={exportExtendedOfflineToken}
      createAuthorizedDevice={createAuthorizedDevice}
      renameAuthorizedDevice={renameAuthorizedDevice}
    />
  );
};

export default ManageDevicesContainer;
